import { attachAuth, authHeaders } from './auth';
/**
 * Typed client for the multi-conversation chat system (/api/chat/*).
 *
 * Model (honor-based): a conversation targets one or more AI REPS (no human participant
 * list). Any human posts identified by the global identity (the "who am I" selector).
 * A conversation can be scoped to an initiative (initiative_id). The list can be filtered
 * by initiative. Reuses the same relative /api base as services/api.ts.
 */
import axios from 'axios';
import { attachRetry } from './httpRetry';

const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  return '/api';
};

const api = axios.create({ baseURL: getApiBaseUrl() });

// The chat API is gated like the rest of /api: without this every conversation call comes
// back 401 and the dock renders as an empty chat with no history.
attachAuth(api);

// Retry idempotent GETs on transient network/DNS failures (DuckDNS resolution is flaky).
attachRetry(api);

// ---- types ----------------------------------------------------------------

export type ParticipantKind = 'human' | 'ai';

// Conversations only carry AI reps as participants (humans speak as the global identity).
export interface ChatParticipant {
  kind: ParticipantKind;
  ref: string; // person slug
}

export interface ChatAgent {
  ref: string; // slug, e.g. 'zee'
  name: string; // display name, e.g. 'Zee'
}

// One `- [ ]` / `- [x]` line parsed out of a message's content, in document order. `id` is
// the durable row to PATCH when a person clicks the rendered checkbox.
export interface ChecklistItem {
  id: number;
  item_index: number;
  text: string;
  checked: boolean;
  task_id?: number | null; // the cockpit Task this item auto-spawned once checked, or null
}

export interface ConversationMessage {
  id: number;
  conversation_id: number;
  role: 'user' | 'assistant';
  sender_kind: ParticipantKind | null;
  sender_ref: string | null;
  content: string;
  checklist_items?: ChecklistItem[]; // any `- [ ]`/`- [x]` lines in `content`, in document order
  created_at: string | null;
  edited_at?: string | null;           // set when the human edits their own message in place (show "edited")
  // Collaboration loop fields:
  corrected?: boolean;                 // this AI message has been corrected (show struck/greyed)
  corrects_message_id?: number | null; // this human message is a correction of that AI message
  endorsed_by?: string | null;         // human label who signed off on this message
  // Feedback / teach loop fields:
  taught?: boolean;                    // this AI message got feedback (show "taught" badge, NOT struck)
  taught_feedback?: string | null;     // the feedback/critique text stored on the taught message
  // Version chain: a revised reply (from teaching) points back at the original AI message it
  // supersedes. The thread renders the LATEST version in place of the original; earlier versions
  // are tucked behind a "previous versions" expander on that slot.
  revises_message_id?: number | null;  // FK -> the original ChatMessage.id this revises
  // QUOTE-REPLY: this message refers to an earlier message in the thread (the human clicked it).
  // `reply_to` is a denormalized preview so the quote renders even when the target isn't loaded.
  reply_to_message_id?: number | null;
  reply_to?: QuotedMessage | null;
}

// The earlier message a quote-reply points at, as carried on the replying message.
export interface QuotedMessage {
  id: number;
  sender_kind: ParticipantKind | null;
  sender_ref: string | null;
  preview: string;
}

export interface LastMessage {
  preview: string;
  sender_kind: ParticipantKind | null;
  sender_ref: string | null;
  created_at: string | null;
}

export interface ConversationSummary {
  id: number;
  title: string;
  kind: 'direct' | 'group';
  created_by: string;
  initiative_id: string | null;
  quest_id: string | null; // Quest this chat's work belongs to; null = not scoped to a quest
  awaiting_human: string | null; // human label the conversation is waiting on, or null
  created_at: string | null;
  updated_at: string | null;
  participants: ChatParticipant[]; // AI reps only
  last_message: LastMessage | null;
  model: string | null; // default Claude model for every task this conversation spawns; null = system default
  pinned: boolean; // pinned conversations always sort first in the list
}

export interface ConversationDetail extends ConversationSummary {
  messages: ConversationMessage[];
}

export interface PostMessageResult {
  messages: ConversationMessage[];
  created_task_id?: number | null;
}

export interface CorrectResult {
  new_message: ConversationMessage;
  corrected_message: ConversationMessage;
  messages: ConversationMessage[];
  saved_to_memory: boolean;
  rep: string;
}

export interface TeachResult {
  taught_message: ConversationMessage;        // the original AI message, now taught=True
  revised_message: ConversationMessage | null; // the regenerated improved reply (null if regen failed)
  saved_to_memory: boolean;
  // A real cockpit task spun off from this feedback (when it read as "actually DO it"), so the
  // rep does the work instead of only rewriting text. null for pure stylistic feedback.
  created_task_id?: number | null;
  rep: string;
  reason?: string;                             // present when revised_message is null
}

export interface EndorseResult {
  message: ConversationMessage;   // the endorsed AI message (includes endorsed_by)
  saved_to_memory: boolean;       // true if the good example was reinforced into the rep's memory
  rep: string;                    // the rep whose memory was reinforced
}

export interface RequestHumanResult {
  conversation: ConversationSummary;
  dependency_id?: number | string | null;
}

// DEEP MODE: the result of spawning a real Claude Code run as a tracked cockpit Task.
//   human_message — the persisted human turn
//   task_id       — the cockpit Task the deep run is tracked as (open it for live output/history)
//   link_message  — the rep's "running as task #N 🔧" message posted into the thread
export interface RunDeepResult {
  human_message: ConversationMessage;
  task_id: number;
  link_message: ConversationMessage;
}

// ---- calls ----------------------------------------------------------------

export const listChatAgents = async (): Promise<ChatAgent[]> => {
  const res = await api.get('/chat/agents');
  return res.data.agents || [];
};

/**
 * List conversations, newest first. Optional `initiativeId` filters to one initiative
 * (all of its conversations over time); optional `createdBy` is a soft "mine" filter.
 */
export const listConversations = async (opts?: {
  initiativeId?: string | null;
  createdBy?: string | null;
}): Promise<ConversationSummary[]> => {
  const params: Record<string, string> = {};
  if (opts?.initiativeId) params.initiative_id = opts.initiativeId;
  if (opts?.createdBy) params.created_by = opts.createdBy;
  const res = await api.get('/chat/conversations', { params });
  return res.data.conversations || [];
};

/**
 * Create a conversation targeting one or more AI reps. One rep = direct, several = group.
 * Optionally scope to an initiative so reps answer in that initiative's context.
 */
export const createConversation = async (
  creator: string,
  reps: string[],
  opts?: { initiativeId?: string | null; questId?: string | null; title?: string }
): Promise<ConversationSummary> => {
  const res = await api.post('/chat/conversations', {
    creator,
    reps,
    initiative_id: opts?.initiativeId || undefined,
    quest_id: opts?.questId || undefined,
    title: opts?.title,
  });
  return res.data;
};

export const getConversation = async (id: number): Promise<ConversationDetail> => {
  const res = await api.get(`/chat/conversations/${id}`);
  return res.data;
};

export const postChatMessage = async (
  conversationId: number,
  sender: string,
  content: string,
  model?: string,
  replyToMessageId?: number | null
): Promise<PostMessageResult> => {
  const payload: any = { sender, content };
  if (model) payload.model = model;
  if (replyToMessageId) payload.reply_to_message_id = replyToMessageId;
  const res = await api.post(`/chat/conversations/${conversationId}/messages`, payload);
  return res.data;
};

/**
 * Recover a dead-end conversation: when the loaded conversation's LAST message is a human turn
 * with no AI reply after it (e.g. the reply was rolled back by a server reload), ask the backend
 * to regenerate the responding reps' replies for that last human turn. Returns the newly created
 * AI message(s); resolves with an empty `messages` array (no-op) if the last message was already
 * an AI reply. 404 if the conversation is missing.
 */
export const retryReply = async (
  conversationId: number,
  model?: string
): Promise<{ messages: ConversationMessage[] }> => {
  const payload: any = {};
  if (model) payload.model = model;
  const res = await api.post(`/chat/conversations/${conversationId}/retry-reply`, payload);
  return res.data;
};

/**
 * DEEP MODE (additive — does NOT touch the quick/streaming send path): ask a rep to ACTUALLY
 * do the work. The backend spawns a real Claude Code process (full tool access, in the rep's
 * scope) as a tracked cockpit Task and posts a "running as task #N 🔧" message into the thread;
 * when the run finishes it reflects the result back as a rep message. Poll/refresh the
 * conversation after calling to pull in the rep's link message + (later) the result message.
 *
 * `rep` is optional: a direct chat uses its sole rep; a group uses the @mentioned rep or the
 * first participant.
 */
export const runDeep = async (
  conversationId: number,
  sender: string,
  content: string,
  rep?: string,
  model?: string
): Promise<RunDeepResult> => {
  const payload: any = { sender, content };
  if (rep) payload.rep = rep;
  if (model) payload.model = model;
  const res = await api.post(`/chat/conversations/${conversationId}/deep`, payload);
  return res.data;
};

// ---- streaming post --------------------------------------------------------

// The events emitted by the NDJSON stream endpoint, in order:
//   human   -> the persisted human message
//   pending -> the set of reps about to reply (UI shows a "typing…" placeholder per rep)
//   context -> EPHEMERAL context-assembly indicator carrying the selected cards/sources
//   reply   -> one rep's reply, the instant THAT rep finishes (pops in incrementally)
//   done    -> stream finished; carries the final created_task_id
//   error   -> something went wrong server-side (caller should fall back)
export type ChatStreamEvent =
  | { type: 'human'; message: ConversationMessage }
  | { type: 'pending'; reps: string[]; created_task_id?: number | null }
  | {
      type: 'context';
      rep?: string;
      data?: {
        card_metadata?: Array<{
          adapter?: string;
          title?: string;
          relevance_score?: number;
          file_count?: number;
        }>;
        [key: string]: any;
      };
    }
  | { type: 'reply'; message: ConversationMessage }
  | { type: 'done'; created_task_id?: number | null }
  | { type: 'error'; detail?: string };

/**
 * Streaming variant of postChatMessage: POSTs to the NDJSON `/messages/stream` endpoint with
 * `fetch` (axios can't stream a response body), reads the body incrementally, and invokes
 * `onEvent` once per complete JSON line so each rep's reply surfaces the instant it finishes.
 *
 * Uses the SAME API base the rest of the app uses (REACT_APP_API_URL or '/api'). Resolves
 * when the stream ends. Throws if streaming is unsupported or the request fails so callers can
 * fall back to the non-stream path.
 */
export const postChatMessageStream = async (
  conversationId: number,
  sender: string,
  content: string,
  onEvent: (event: ChatStreamEvent) => void,
  model?: string,
  signal?: AbortSignal,
  replyToMessageId?: number | null
): Promise<void> => {
  const payload: any = { sender, content };
  if (model) payload.model = model;
  if (replyToMessageId) payload.reply_to_message_id = replyToMessageId;

  const url = `${getApiBaseUrl()}/chat/conversations/${conversationId}/messages/stream`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`stream request failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const flushLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    try {
      onEvent(JSON.parse(trimmed) as ChatStreamEvent);
    } catch {
      // Ignore a malformed/partial line (shouldn't happen with proper newline framing).
    }
  };

  // Read chunks, split on newlines, JSON.parse each complete line. The trailing fragment after
  // the last newline is buffered until the next chunk completes it.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nlIndex: number;
    while ((nlIndex = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, nlIndex);
      buffer = buffer.slice(nlIndex + 1);
      flushLine(line);
    }
  }
  // Flush any trailing buffered line (in case the stream didn't end with a newline).
  buffer += decoder.decode();
  if (buffer.trim()) flushLine(buffer);
};

// ---- orchestrated send (auto-deciding) -------------------------------------

// The events emitted by the ORCHESTRATOR NDJSON stream. The rep runs a BOUNDED ITERATIVE LOOP,
// so MANY `status` events stream per turn (planning → searching → reading … → answering) before
// the single substantive reply/confirm lands:
//   human   -> the persisted human message
//   status  -> EPHEMERAL step indicator (planning/searching/reading the X section/re-planning/
//              answering); streams repeatedly during the loop; NEVER persisted; cleared on done
//   context -> EPHEMERAL context-assembly indicator carrying the selected cards/sources for the
//              turn; rendered like a status line; never persisted
//   reply   -> one rep's reply (incl. a "🔧 running as task #N" task-link reply for a goal-driven
//              deep run; that reply carries task_id + goal + max_turns)
//   confirm -> the rep is asking the human to confirm before acting (persisted question + buttons)
//   done    -> stream finished
//   error   -> something went wrong server-side (caller should fall back to postChatMessageStream)
export type ChatOrchestrateEvent =
  | { type: 'human'; message: ConversationMessage }
  | { type: 'status'; rep: string; text: string }
  | {
      type: 'context';
      rep: string;
      data?: {
        card_metadata?: Array<{
          adapter?: string;
          title?: string;
          relevance_score?: number;
          file_count?: number;
        }>;
        [key: string]: any;
      };
    }
  | {
      type: 'reply';
      message: ConversationMessage;
      task_id?: number;
      deep_subtasks?: Array<{ task_id: number; goal?: string | null; deep_brief?: string }>;
      goal?: string | null;
      max_turns?: number;
      deep_brief?: string;
      rep?: string;
    }
  | {
      type: 'confirm';
      rep: string;
      question: string;
      message: ConversationMessage;
      proposed_action?: { deep_brief?: string | null; rationale?: string | null };
    }
  | { type: 'done' }
  | { type: 'error'; detail?: string };

/**
 * AUTO-DECIDING send: POSTs to the NDJSON `/orchestrate` endpoint. The rep's orchestrator runs a
 * fast planner per message and decides whether to answer from grounded context, gather more files,
 * spawn a real task, or ask the human to confirm — streaming events as it goes. Same fetch+NDJSON
 * reader shape as postChatMessageStream; resolves when the stream ends. Throws if the request
 * fails so callers can fall back to postChatMessageStream.
 */
export const orchestrateMessage = async (
  conversationId: number,
  sender: string,
  content: string,
  onEvent: (event: ChatOrchestrateEvent) => void,
  model?: string,
  signal?: AbortSignal,
  replyToMessageId?: number | null
): Promise<void> => {
  const payload: any = { sender, content };
  if (model) payload.model = model;
  if (replyToMessageId) payload.reply_to_message_id = replyToMessageId;

  const url = `${getApiBaseUrl()}/chat/conversations/${conversationId}/orchestrate`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`orchestrate request failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const flushLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    try {
      onEvent(JSON.parse(trimmed) as ChatOrchestrateEvent);
    } catch {
      // Ignore a malformed/partial line (shouldn't happen with proper newline framing).
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nlIndex: number;
    while ((nlIndex = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, nlIndex);
      buffer = buffer.slice(nlIndex + 1);
      flushLine(line);
    }
  }
  buffer += decoder.decode();
  if (buffer.trim()) flushLine(buffer);
};

/**
 * Edit a HUMAN message you already sent, then regenerate the rep's reply against the new text.
 * POSTs to the NDJSON `/messages/{messageId}/edit` endpoint. The backend edits the message in
 * place (stamping `edited_at`), truncates the now-stale tail, and re-streams the regenerated reply
 * via the SAME auto-deciding orchestrator the normal send uses — so the event protocol is identical
 * to `orchestrateMessage` (human -> status -> reply(s) -> done). Same fetch+NDJSON reader shape.
 * Throws on a failed request (e.g. 400/404) so callers can surface the error and reload.
 */
export const editMessageStream = async (
  conversationId: number,
  messageId: number,
  editor: string,
  content: string,
  onEvent: (event: ChatOrchestrateEvent) => void,
  model?: string,
  signal?: AbortSignal
): Promise<void> => {
  const payload: any = { editor, content };
  if (model) payload.model = model;

  const url = `${getApiBaseUrl()}/chat/conversations/${conversationId}/messages/${messageId}/edit`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`edit request failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const flushLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    try {
      onEvent(JSON.parse(trimmed) as ChatOrchestrateEvent);
    } catch {
      // Ignore a malformed/partial line (shouldn't happen with proper newline framing).
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nlIndex: number;
    while ((nlIndex = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, nlIndex);
      buffer = buffer.slice(nlIndex + 1);
      flushLine(line);
    }
  }
  buffer += decoder.decode();
  if (buffer.trim()) flushLine(buffer);
};

export const renameConversation = async (
  id: number,
  title: string
): Promise<ConversationSummary> => {
  const res = await api.put(`/chat/conversations/${id}`, { title });
  return res.data;
};

/**
 * Set (or clear) the conversation's default Claude model. This model applies to EVERY task
 * the conversation spawns; pass an empty string to clear it back to the system default.
 * Chat replies are unaffected (they keep using the system default).
 */
export const updateConversationModel = async (
  id: number,
  model: string
): Promise<ConversationSummary> => {
  const res = await api.put(`/chat/conversations/${id}/model`, { model: model || null });
  return res.data;
};

/** One selectable quest in a quest picker. `outcome` is the display name: Quest has no
 * separate title field, so every Quest surface shows the outcome text. */
export interface QuestOption {
  quest_id: string;
  outcome: string;
  completed: boolean;
  team_id: string;
  team_name: string;
}

/**
 * Every quest the cockpit can see, flattened across teams, open ones first. Served from a
 * 5-minute backend cache, so calling this on mount is cheap. Returns [] (never throws a page
 * down) when the Teams API is unreachable; the picker then just shows no quests.
 */
export const listQuests = async (refresh = false): Promise<QuestOption[]> => {
  const res = await api.get('/quests', { params: refresh ? { refresh: true } : {} });
  return res.data.quests || [];
};

/**
 * Scope (or unscope) a conversation to a Quest. Pass '' to clear. A scoped conversation puts
 * that quest's outcome, team and id into every rep's prompt, so the rep mails the quest's own
 * people without anyone pasting the id.
 */
export const setConversationQuest = async (
  id: number,
  questId: string
): Promise<ConversationSummary> => {
  const res = await api.put(`/chat/conversations/${id}/quest`, { quest_id: questId || null });
  return res.data;
};

/** Pin (or unpin) a conversation so it always sorts first in the list, reachable without
 * scrolling regardless of how many other conversations exist below it. */
export const setConversationPinned = async (
  id: number,
  pinned: boolean
): Promise<ConversationSummary> => {
  const res = await api.put(`/chat/conversations/${id}/pinned`, { pinned });
  return res.data;
};

/** Add an AI rep to a conversation. Returns the updated conversation (kind may flip to 'group'). */
export const addConversationParticipant = async (
  id: number,
  ref: string
): Promise<ConversationSummary> => {
  const res = await api.post(`/chat/conversations/${id}/participants`, { ref });
  return res.data;
};

/**
 * Remove an AI rep from a conversation. Returns the updated conversation (kind may revert to
 * 'direct'). The backend refuses (400) removing the LAST remaining rep — a chat needs ≥1 rep.
 */
export const removeConversationParticipant = async (
  id: number,
  ref: string
): Promise<ConversationSummary> => {
  const res = await api.delete(`/chat/conversations/${id}/participants/${ref}`);
  return res.data;
};

/** Delete a whole conversation (and its messages + participants). 404 if it's already gone. */
export const deleteConversation = async (id: number): Promise<void> => {
  await api.delete(`/chat/conversations/${id}`);
};

/** Delete a single message from a conversation. 404 if it isn't in that conversation. */
export const deleteMessage = async (
  conversationId: number,
  messageId: number
): Promise<void> => {
  await api.delete(`/chat/conversations/${conversationId}/messages/${messageId}`);
};

// ---- AI<->human collaboration loop ----------------------------------------

/**
 * Correct & teach: a human fixes an AI rep's message. The original is marked corrected,
 * a new human message holds the correction, and the lesson is saved to the rep's AI memory.
 */
export const correctMessage = async (
  conversationId: number,
  messageId: number,
  payload: { corrector: string; corrected_content: string; lesson?: string }
): Promise<CorrectResult> => {
  const res = await api.post(
    `/chat/conversations/${conversationId}/messages/${messageId}/correct`,
    payload
  );
  return res.data;
};

/**
 * Give feedback / teach: a human critiques an AI rep's reply. The feedback is saved to the
 * rep's AI memory, the original is marked "taught" (kept visible, NOT struck), and an improved
 * reply is regenerated from the rep and returned as `revised_message`.
 */
export const teachMessage = async (
  conversationId: number,
  messageId: number,
  payload: { teacher: string; feedback: string; model?: string }
): Promise<TeachResult> => {
  const res = await api.post(
    `/chat/conversations/${conversationId}/messages/${messageId}/teach`,
    payload
  );
  return res.data;
};

/**
 * Endorse an AI message — sets endorsed_by AND reinforces the rep's AI memory with this
 * good example (the positive counterpart to teach/correct).
 */
export const endorseMessage = async (
  conversationId: number,
  messageId: number,
  endorser: string
): Promise<EndorseResult> => {
  const res = await api.post(
    `/chat/conversations/${conversationId}/messages/${messageId}/endorse`,
    { endorser }
  );
  return res.data;
};

/**
 * Toggle one checkbox rendered out of a chat message's `- [ ]`/`- [x]` lines. Persists
 * immediately server-side; returns the updated item so the caller can reconcile local state
 * if it optimistically toggled first.
 */
export const setChecklistItemChecked = async (
  conversationId: number,
  messageId: number,
  itemId: number,
  checked: boolean
): Promise<ChecklistItem> => {
  const res = await api.patch(
    `/chat/conversations/${conversationId}/messages/${messageId}/checklist-items/${itemId}`,
    { checked }
  );
  return res.data;
};

/** Bring in a real human: flags awaiting_human + files a dependency request for them. */
export const requestHuman = async (
  conversationId: number,
  payload: { human: string; reason: string; by: string }
): Promise<RequestHumanResult> => {
  const res = await api.post(`/chat/conversations/${conversationId}/request-human`, payload);
  return res.data;
};

/**
 * Convenience: find-or-create a DIRECT conversation between the current identity and a
 * single AI rep. Used by the "Chat with X's AI" buttons so they reuse one thread.
 */
export const openDirectConversation = async (
  creator: string,
  repRef: string
): Promise<ConversationSummary> => {
  const convs = await listConversations();
  const existing = convs.find(
    (c) =>
      c.kind === 'direct' &&
      c.created_by === creator &&
      c.participants.some((p) => p.kind === 'ai' && p.ref === repRef)
  );
  if (existing) return existing;
  return createConversation(creator, [repRef]);
};
